import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import API from "../../config/config";
import { toast } from "react-toastify";
import { compareNames } from "../Driver/compareNames";
import { isValidFullName } from "../../utils/Validator";

const StepAadhaar = forwardRef(
  ({ formData, setFormData, driver, disabled, initialFormData }, ref) => {
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem("adminToken");

    const handleChange = (e) => {
      const { name, value } = e.target;
      if (name === "aadhaarNumber") {
        const numericValue = value.replace(/\D/g, "");
        if (numericValue.length <= 12) {
          setFormData((prev) => ({ ...prev, [name]: numericValue }));
        }
        return;
      }
      if (name === "aadhaarNameToMatch") {
        const alphaValue = value.replace(/[^A-Za-z\s]/g, "");
        setFormData((prev) => ({ ...prev, [name]: alphaValue }));
        return;
      }
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const isChanged = () => {
      if (!initialFormData) return true;

      return (
        formData.aadhaarNumber !== initialFormData.aadhaarNumber ||
        formData.aadhaarNameToMatch !== initialFormData.aadhaarNameToMatch ||
        formData.aadhaarFront !== initialFormData.aadhaarFront ||
        formData.aadhaarBack !== initialFormData.aadhaarBack
      );
    };

    const handleFileChange = (e, key) => {
      const file = e.target.files[0];
      if (file) {
        setFormData((prev) => ({ ...prev, [key]: file }));
      }
    };

    useEffect(() => {
      const dlName =
        driver?.drivingLincenseDetails?.nameToMatch?.trim() ||
        formData?.nameToMatch?.trim();

      if (!formData.aadhaarNameToMatch && dlName) {
        setFormData((prev) => ({
          ...prev,
          aadhaarNameToMatch: dlName,
        }));
      }
    }, []);

    const uploadFile = async (file, type) => {
      const headers = new Headers();
      headers.append("Authorization", "Bearer " + token);

      const formDataUpload = new FormData();
      formDataUpload.append("documentFor", "Driver");
      formDataUpload.append("type", type);
      formDataUpload.append("ownerId", driver._id);
      formDataUpload.append("file", file, file.name);

      const response = await fetch(API.ADMIN.ADMINDOCUMENT_UPLOAD, {
        method: "POST",
        headers,
        body: formDataUpload,
      });

      if (!response.ok) {
        throw new Error(`${type} upload failed`);
      }
      const data = await response.json();
      return data.document?.documentUrl;
    };

    // This function will be called by parent Next button
    useImperativeHandle(ref, () => ({
      validateAndUpload: async () => {
        const { aadhaarNumber, aadhaarNameToMatch, aadhaarFront, aadhaarBack } =
          formData;

        // Check if all required fields are filled
        const requiredFields = [
          { field: aadhaarNumber, label: "Aadhaar Number" },
          { field: aadhaarNameToMatch, label: "Full Name" },
          { field: aadhaarFront, label: "Aadhaar Front Image" },
          { field: aadhaarBack, label: "Aadhaar Back Image" },
        ];

        for (const { field, label } of requiredFields) {
          if (!field) {
            toast.error(`Please provide ${label}.`);
            throw new Error(`Please provide ${label}.`);
          }
        }

        // Validate name format (only letters and spaces)
        if (!isValidFullName(aadhaarNameToMatch)) {
          toast.error("Full Name must contain only letters and spaces.");
          throw new Error("Full Name must contain only letters and spaces.");
        }

        console.log("Debug - Driver object:", driver);
        console.log("Debug - DL details:", driver?.drivingLincenseDetails);
        console.log(
          "Debug - DL nameToMatch:",
          driver?.drivingLincenseDetails?.nameToMatch
        );

        // Validate name matching with driving license
        if (
          driver?.drivingLincenseDetails?.nameToMatch &&
          !compareNames(
            aadhaarNameToMatch,
            driver.drivingLincenseDetails.nameToMatch
          )
        ) {
          console.log("Name validation failed:");
          console.log("Aadhaar Name:", aadhaarNameToMatch);
          console.log("DL Name:", driver.drivingLincenseDetails.nameToMatch);
          console.log(
            "CompareNames result:",
            compareNames(
              aadhaarNameToMatch,
              driver.drivingLincenseDetails.nameToMatch
            )
          );

          toast.error(
            "The name on Aadhaar does not match the name on your Driving License. Please enter the correct name."
          );
          throw new Error(
            "The name on Aadhaar does not match the name on your Driving License. Please enter the correct name."
          );
        }

        const changed = isChanged();

        if (!changed) {
          return "unchanged";
        }

        setLoading(true);
        try {
          const frontUrl =
            typeof aadhaarFront === "string"
              ? aadhaarFront
              : await uploadFile(aadhaarFront, "aadhaar_front");

          const backUrl =
            typeof aadhaarBack === "string"
              ? aadhaarBack
              : await uploadFile(aadhaarBack, "aadhaar_back");

          const payload = {
            ownerId: driver._id,
            aadhaarNumber,
            nameToMatch: aadhaarNameToMatch,
            consent: "Y",
            consentText:
              "I hereby declare my consent agreement for fetching my information via ZOOP API",
            taskId: "08b01aa8-9487-4e6d-a0f0-c796839d6b77",
            aadhaarfront: frontUrl,
            aadhaarback: backUrl,
          };

          const response = await fetch(API.ADMIN.ADMIN_VERIFY_AADHAAR, {
            method: "POST",
            headers: {
              Authorization: "Bearer " + token,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });

          const result = await response.json();

          if (!response.ok || !result.success) {
            throw new Error(result.message || "Aadhaar submission failed");
          }

          // Update formData with URLs returned
          setFormData((prev) => ({
            ...prev,
            aadhaarFront: frontUrl,
            aadhaarBack: backUrl,
            aadhaarNameToMatch,
            aadhaarNumber,
          }));
          toast.success("Aadhaar details submitted successfully.");
          return true; // success
        } finally {
          setLoading(false);
        }
      },
    }));

    return (
      <div className="max-w-4xl mx-auto p-4 bg-white rounded shadow text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Aadhaar Number */}
          <div>
            <label className="block mb-1 text-gray-700 font-medium text-sm">
              Aadhaar Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="aadhaarNumber"
              value={formData.aadhaarNumber || ""}
              onChange={handleChange}
              placeholder="Enter Aadhaar Number"
              className="w-full border px-2 py-1 rounded text-sm"
              required
              disabled={loading || disabled}
            />
          </div>

          {/* Name on Aadhaar */}
          <div>
            <label className="block mb-1 text-gray-700 font-medium text-sm">
              Name on Aadhaar <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="aadhaarNameToMatch"
              value={formData.aadhaarNameToMatch || ""}
              onChange={handleChange}
              placeholder="Enter Name as on Aadhaar"
              className="w-full border px-2 py-1 rounded text-sm"
              required
              disabled={loading || disabled}
            />
          </div>

          {/* Aadhaar Front Upload */}
          <div>
            <label className="block mb-1 text-gray-700 font-medium text-sm">
              Upload Aadhaar Front <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => handleFileChange(e, "aadhaarFront")}
              className="w-full border px-2 py-1 rounded text-sm"
              required={!formData.aadhaarFront}
              disabled={loading || disabled}
            />
            {formData.aadhaarFront && (
              <img
                src={
                  typeof formData.aadhaarFront === "string"
                    ? formData.aadhaarFront
                    : URL.createObjectURL(formData.aadhaarFront)
                }
                alt="Aadhaar Front"
                className="w-24 mt-1 border rounded"
              />
            )}
          </div>

          {/* Aadhaar Back Upload */}
          <div>
            <label className="block mb-1 text-gray-700 font-medium text-sm">
              Upload Aadhaar Back <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => handleFileChange(e, "aadhaarBack")}
              className="w-full border px-2 py-1 rounded text-sm"
              required={!formData.aadhaarBack}
              disabled={loading || disabled}
            />
            {formData.aadhaarBack && (
              <img
                src={
                  typeof formData.aadhaarBack === "string"
                    ? formData.aadhaarBack
                    : URL.createObjectURL(formData.aadhaarBack)
                }
                alt="Aadhaar Back"
                className="w-24 mt-1 border rounded"
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default StepAadhaar;
